import React from 'react'
import {
  LayoutDashboard,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Filter,
  RefreshCw,
  TrendingUp,
  Eye,
  Edit3,
  Activity,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Database,
  Zap,
  Info,
  Mail,
  ArrowRight,
  SlidersHorizontal,
  BarChart3,
  Upload,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Annotated visual: Stats Cards (matches real Dashboard stat cards)
// ---------------------------------------------------------------------------
function StatCardsMockup() {
  const cards = [
    { label: 'Total Documents', value: '1,247', icon: FileText, iconBg: 'bg-blue-50 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400', sub: 'All time' },
    { label: 'Pending Reviews', value: '12', icon: Clock, iconBg: 'bg-orange-50 dark:bg-orange-900/30', iconColor: 'text-orange-500 dark:text-orange-400', sub: 'All time' },
    { label: 'Completed', value: '1,180', icon: CheckCircle, iconBg: 'bg-green-50 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400', sub: 'All time' },
    { label: 'Cancelled', value: '55', icon: AlertCircle, iconBg: 'bg-gray-100 dark:bg-gray-700', iconColor: 'text-gray-500 dark:text-gray-400', sub: 'All time' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, i) => {
        const Icon = card.icon
        return (
          <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className={`w-12 h-12 ${card.iconBg} rounded-xl flex items-center justify-center`}>
              <Icon className={`h-6 w-6 ${card.iconColor}`} />
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">{card.value}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{card.sub}</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Annotated visual: Charts Row (Processing Trend line + Status Donut)
// ---------------------------------------------------------------------------
function ChartsRowMockup() {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const values = [3, 7, 5, 9, 6, 2, 4]
  const maxValue = 10

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Processing Trend - 2 cols - LINE CHART */}
      <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Processing Trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last 7 days activity</p>
          </div>
          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">View Details →</span>
        </div>
        {/* Line chart with data */}
        <div className="h-48 relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-8 w-8 flex flex-col justify-between text-[10px] text-gray-400 dark:text-gray-500">
            <span>10</span>
            <span>8</span>
            <span>6</span>
            <span>4</span>
            <span>2</span>
            <span>0</span>
          </div>
          {/* Chart grid + line */}
          <div className="ml-10 h-full pb-8 relative">
            {/* Dashed grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{ bottom: '32px' }}>
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div key={i} className="border-b border-gray-100 dark:border-gray-800 border-dashed" />
              ))}
            </div>
            {/* SVG Line chart with area fill */}
            <svg className="absolute inset-0 w-full" style={{ height: 'calc(100% - 32px)' }} viewBox="0 0 600 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="guideLineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Area fill */}
              <path
                d={`M 0 ${200 - (values[0] / maxValue) * 180} ${values.map((v, i) => `L ${(i / 6) * 600} ${200 - (v / maxValue) * 180}`).join(' ')} L 600 200 L 0 200 Z`}
                fill="url(#guideLineGradient)"
              />
              {/* Line */}
              <path
                d={`M 0 ${200 - (values[0] / maxValue) * 180} ${values.map((v, i) => `L ${(i / 6) * 600} ${200 - (v / maxValue) * 180}`).join(' ')}`}
                fill="none"
                stroke="#1e3a5f"
                strokeWidth="2.5"
              />
              {/* Data points */}
              {values.map((v, i) => (
                <circle
                  key={i}
                  cx={(i / 6) * 600}
                  cy={200 - (v / maxValue) * 180}
                  r="4"
                  fill="white"
                  stroke="#1e3a5f"
                  strokeWidth="2"
                />
              ))}
            </svg>
            {/* X-axis labels */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-gray-400 dark:text-gray-500">
              {days.map((day, i) => (
                <span key={i}>{day}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution - 1 col - DONUT CHART */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Status Distribution</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">All time document status</p>
          </div>
          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">View All</span>
        </div>
        {/* Donut + Legend side by side */}
        <div className="flex items-center gap-4">
          <div className="relative w-32 h-32 flex-shrink-0">
            {/* SVG donut ring - full circle using strokeDasharray */}
            <svg viewBox="0 0 200 200" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
              {/* Completed: 85% of circumference (439.82) = 373.85 */}
              <circle cx="100" cy="100" r="70" fill="none" stroke="#22c55e" strokeWidth="28" strokeDasharray="373.85 66" strokeDashoffset="0" />
              {/* Reviewing: 8% = 35.19 */}
              <circle cx="100" cy="100" r="70" fill="none" stroke="#f97316" strokeWidth="28" strokeDasharray="35.19 404.63" strokeDashoffset="-373.85" />
              {/* Processing: 7% = 30.79 */}
              <circle cx="100" cy="100" r="70" fill="none" stroke="#1e3a5f" strokeWidth="28" strokeDasharray="30.79 409.03" strokeDashoffset="-409.04" />
            </svg>
            {/* Center total */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">1,247</p>
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="space-y-2">
            {[
              { label: 'Completed', color: 'bg-green-500' },
              { label: 'Reviewing', color: 'bg-orange-500' },
              { label: 'Processing', color: 'bg-navy-800' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${s.color}`} />
                <span className="text-xs text-gray-600 dark:text-gray-400">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Annotated visual: Documents Table (matches real Dashboard table exactly)
// ---------------------------------------------------------------------------
function DocumentsTableMockup() {
  const rows = [
    { name: 'Invoice_Q1_2026.pdf', desc: 'Supplier invoice for Q1', docs: 2, date: '15 May 2026', time: '10:32', status: 'Completed', statusBg: 'bg-green-50 dark:bg-green-900/20', statusText: 'text-green-600 dark:text-green-300', statusDot: 'bg-green-500', confidence: 96, createdBy: 'John Smith', initials: 'JS' },
    { name: 'PO_Agreement_Draft.pdf', desc: 'Purchase order agreement', docs: 1, date: '15 May 2026', time: '09:14', status: 'Reviewing', statusBg: 'bg-orange-50 dark:bg-orange-900/20', statusText: 'text-orange-600 dark:text-orange-300', statusDot: 'bg-orange-500', confidence: 82, createdBy: 'Jane Doe', initials: 'JD' },
    { name: 'Contract_Renewal.docx', desc: 'Annual contract renewal', docs: 3, date: '14 May 2026', time: '16:50', status: 'Processing', statusBg: 'bg-blue-50 dark:bg-blue-900/20', statusText: 'text-blue-600 dark:text-blue-300', statusDot: 'bg-blue-500', confidence: 0, createdBy: 'Mike Brown', initials: 'MB' },
  ]

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header + Filters */}
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recently Processed Documents</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Latest document processing activity</p>
        </div>
        {/* Filters Row - labeled groups */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">Search</label>
            <div className="h-8 min-w-[180px] bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 flex items-center">
              <span className="text-[10px] text-gray-400">Title, description, documents...</span>
            </div>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">Date Range</label>
            <div className="flex items-center gap-1.5">
              <div className="h-8 px-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center">
                <span className="text-[10px] text-gray-400">mm/dd/yyyy</span>
              </div>
              <div className="h-8 px-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center">
                <span className="text-[10px] text-gray-400">mm/dd/yyyy</span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <div className="h-8 min-w-[120px] bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 flex items-center">
              <span className="text-[10px] text-gray-400">All Status</span>
            </div>
          </div>
          <div className="h-8 px-4 bg-navy-800 dark:bg-navy-700 rounded-lg flex items-center gap-1.5">
            <Filter className="h-3 w-3 text-white" />
            <span className="text-[10px] text-white font-medium">Apply</span>
          </div>
          <div className="h-8 w-8 flex items-center justify-center border border-gray-200 dark:border-gray-700 rounded-lg">
            <RefreshCw className="h-3 w-3 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
              {['Document Name', 'Processed Date', 'Status', 'AI Confidence', 'Created By', ''].map((col, i) => (
                <th key={i} className="px-4 py-2.5 text-left text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                {/* Document Name with icon + count badge */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center relative">
                      <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[8px] font-medium rounded-full w-4 h-4 flex items-center justify-center">{row.docs}</span>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-gray-900 dark:text-gray-100">{row.name}</p>
                      <p className="text-[9px] text-gray-500 dark:text-gray-400">{row.desc}</p>
                    </div>
                  </div>
                </td>
                {/* Processed Date */}
                <td className="px-4 py-3">
                  <p className="text-[11px] text-gray-900 dark:text-gray-100">{row.date}</p>
                  <p className="text-[9px] text-gray-500 dark:text-gray-400">{row.time}</p>
                </td>
                {/* Status badge with dot */}
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${row.statusBg} ${row.statusText}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${row.statusDot}`} />
                    {row.status}
                  </span>
                </td>
                {/* AI Confidence with progress bar */}
                <td className="px-4 py-3">
                  {row.confidence > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${row.confidence >= 90 ? 'bg-green-500' : row.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${row.confidence}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-600 dark:text-gray-400">{row.confidence}%</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-gray-400">-</span>
                  )}
                </td>
                {/* Created By with avatar */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] font-medium text-gray-600 dark:text-gray-300">{row.initials}</div>
                    <span className="text-[10px] text-gray-600 dark:text-gray-400">{row.createdBy}</span>
                  </div>
                </td>
                {/* Actions */}
                <td className="px-4 py-3">
                  {row.status === 'Completed' ? (
                    <Eye className="h-3.5 w-3.5 text-gray-400" />
                  ) : (
                    <Edit3 className="h-3.5 w-3.5 text-gray-400" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400">
          <span>Showing 1-10 of 1,247 results</span>
          <span className="h-3 w-px bg-gray-200 dark:bg-gray-700" />
          <span className="flex items-center gap-1">
            Rows per page
            <span className="px-1.5 py-0.5 border border-gray-200 dark:border-gray-700 rounded text-[9px]">10</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><ChevronLeft className="h-3 w-3" /> Previous</span>
          <span className="w-6 h-6 rounded bg-navy-800 text-white text-[10px] flex items-center justify-center font-medium">1</span>
          <span className="w-6 h-6 rounded text-gray-500 text-[10px] flex items-center justify-center">2</span>
          <span className="w-6 h-6 rounded text-gray-500 text-[10px] flex items-center justify-center">3</span>
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">Next <ChevronRight className="h-3 w-3" /></span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Annotated visual: Bottom Cards (matches real Dashboard exactly)
// ---------------------------------------------------------------------------
function BottomCardsMockup() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h2>
          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">View All</span>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {[
            { text: 'Invoice Q1 published', iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400', icon: CheckCircle, time: '2m ago' },
            { text: 'New email received', iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400', icon: Upload, time: '5m ago' },
            { text: 'Template fields updated', iconBg: 'bg-orange-100 dark:bg-orange-900/30', iconColor: 'text-orange-600 dark:text-orange-400', icon: Edit3, time: '12m ago' },
            { text: 'Request cancelled', iconBg: 'bg-red-100 dark:bg-red-900/30', iconColor: 'text-red-600 dark:text-red-400', icon: AlertCircle, time: '1h ago' },
          ].map((item, i) => {
            const Icon = item.icon
            return (
              <div key={i} className="flex items-center gap-3 px-5 py-3">
                <div className={`w-8 h-8 rounded-lg ${item.iconBg} flex items-center justify-center`}>
                  <Icon className={`h-4 w-4 ${item.iconColor}`} />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{item.text}</span>
                <span className="text-xs text-gray-400">{item.time}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quick Actions (6 links) */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h2>
        </div>
        <div className="p-4 space-y-1">
          {[
            { text: 'View All Requests', sub: 'Browse and manage documents', icon: FileText, color: 'bg-blue-50 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
            { text: 'Email Queue', sub: 'Review incoming emails', icon: Upload, color: 'bg-purple-50 dark:bg-purple-900/30', iconColor: 'text-purple-600 dark:text-purple-400' },
            { text: 'Templates', sub: 'Manage extraction templates', icon: TrendingUp, color: 'bg-green-50 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
            { text: 'Activity Log', sub: 'Full audit trail', icon: Activity, color: 'bg-orange-50 dark:bg-orange-900/30', iconColor: 'text-orange-600 dark:text-orange-400' },
            { text: 'Documents', sub: 'View all extracted documents', icon: Database, color: 'bg-indigo-50 dark:bg-indigo-900/30', iconColor: 'text-indigo-600 dark:text-indigo-400' },
            { text: 'Settings', sub: 'Configure organisation preferences', icon: SlidersHorizontal, color: 'bg-gray-100 dark:bg-gray-800', iconColor: 'text-gray-600 dark:text-gray-400' },
          ].map((action, i) => {
            const Icon = action.icon
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className={`w-8 h-8 rounded-lg ${action.color} flex items-center justify-center`}>
                  <Icon className={`h-4 w-4 ${action.iconColor}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{action.text}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">{action.sub}</p>
                </div>
                <ArrowUpRight className="h-3.5 w-3.5 text-gray-400" />
              </div>
            )
          })}
        </div>
      </div>

      {/* System Health (5 metrics) */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">System Health</h2>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Healthy</span>
        </div>
        <div className="p-4 space-y-4">
          {[
            { label: 'Database Latency', value: '12ms', pct: 97, showBar: true },
            { label: 'Azure Storage', value: 'Connected', pct: 100, showBar: false },
            { label: 'AI Service', value: 'Configured', pct: 100, showBar: false },
            { label: 'Queue Processing', value: 'Normal', pct: 100, showBar: false },
            { label: 'Connection Pool', value: '2 / 10', pct: 20, showBar: true },
          ].map((m, i) => (
            <div key={i}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600 dark:text-gray-400">{m.label}</span>
                <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{m.value}</span>
              </div>
              {m.showBar && (
                <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full mt-1.5">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${m.pct}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dashboard Guide Content
// ---------------------------------------------------------------------------
export default function DashboardGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <LayoutDashboard className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Your central hub for monitoring document processing, system performance, and recent activity.</p>
        </div>
      </div>

      {/* Section 1: Page Header */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Page Header & Controls</h3>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dashboard</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Overview of processed documents and system performance</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                <Clock className="h-3 w-3" />
                Last updated: 10:32 AM
              </div>
              <div className="h-7 px-2.5 flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <RefreshCw className="h-3 w-3 text-gray-500" />
                <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Refresh</span>
              </div>
            </div>
          </div>
          {/* Export & Date Filter */}
          <div className="flex items-center gap-2 mt-3 justify-end">
            <div className="h-7 px-2.5 flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <Download className="h-3 w-3 text-gray-500" />
              <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Export Report</span>
            </div>
            <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
            <Filter className="h-3 w-3 text-gray-400" />
            <div className="h-7 px-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
              <span className="text-[10px] text-gray-400">2026-05-01</span>
            </div>
            <span className="text-[10px] text-gray-400">to</span>
            <div className="h-7 px-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
              <span className="text-[10px] text-gray-400">2026-05-15</span>
            </div>
            <div className="h-7 px-2.5 bg-navy-800 rounded-lg flex items-center">
              <span className="text-[10px] text-white font-medium">Apply</span>
            </div>
          </div>
        </div>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>• <strong>Last Updated</strong> shows when data was last refreshed. The dashboard auto-refreshes every 60 seconds silently without disrupting your workflow.</p>
          <p>• <strong>Refresh</strong> manually triggers a data refresh.</p>
          <p>• <strong>Export Report</strong> downloads a CSV report of all requests within the selected date range.</p>
          <p>• <strong>Date Filter</strong> filters the KPI stats cards by a custom date range. Click Apply to activate, and the filter badge will confirm the active range.</p>
        </div>
      </section>

      {/* Section 2: Stats Cards */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">KPI Stats Cards</h3>
        </div>
        <StatCardsMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Four key metrics at a glance:</p>
          <p>• <strong>Total Documents</strong> total number of extraction requests ever created (or within the active date filter).</p>
          <p>• <strong>Pending Reviews</strong> documents in "Reviewing" status awaiting human verification.</p>
          <p>• <strong>Completed</strong> successfully processed and approved documents.</p>
          <p>• <strong>Cancelled</strong> requests that were cancelled or rejected.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">These stats update in real-time and respect the global date filter when applied.</p>
        </div>
      </section>

      {/* Section 3: Charts */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Processing Trend & Status Distribution</h3>
        </div>
        <ChartsRowMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>• <strong>Processing Trend</strong> (left, 2/3 width) line chart showing daily processing volume over the last 7 days. Includes Y-axis scale, grid lines, and data points connected with an area fill gradient. Helps identify peak days and processing patterns.</p>
          <p>• <strong>Status Distribution</strong> (right, 1/3 width) donut chart breaking down all requests by their current status (Completed, Reviewing, Processing, Pending, Cancelled). The centre shows the total count. Legend with counts is displayed beside the chart.</p>
          <p>• Click <strong>"View Details"</strong> on the trend chart or <strong>"View All"</strong> on the donut to navigate to the full Requests page with that filter pre-applied.</p>
        </div>
      </section>

      {/* Section 4: Documents Table */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">4</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recently Processed Documents Table</h3>
        </div>
        <DocumentsTableMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>A full-featured data table with server-side filtering, sorting, and pagination:</p>
          <p>• <strong>Search</strong> filter by document title, description, or document names.</p>
          <p>• <strong>Date Range</strong> two date pickers to narrow results to a specific time window.</p>
          <p>• <strong>Status Filter</strong> dropdown with options: All Status, Pending, Processing, Extracted, Reviewing, Approved, Rejected, Failed, Completed, Cancelled.</p>
          <p>• <strong>Apply</strong> button sends the current filters to the server. The <strong>Reset</strong> button (circular arrow) clears all table filters.</p>
          <p>• <strong>Column Sorting</strong> click any column header (Document Name, Processed Date, Status, AI Confidence) to sort ascending/descending. An arrow icon shows sort direction.</p>
          <p>• <strong>Document Name column</strong> shows a colored file icon with a document count badge, the request title, and a short description.</p>
          <p>• <strong>AI Confidence</strong> shows a mini progress bar alongside the percentage. Color: green (90%+), yellow (70-89%), red (&lt;70%).</p>
          <p>• <strong>Created By</strong> shows the user avatar initials circle and their name.</p>
          <p>• <strong>Actions</strong> an eye icon (View) for completed/cancelled requests, or a pencil icon (Edit) for in-progress requests.</p>
          <p>• <strong>Pagination</strong> shows "Showing X-Y of Z results", a "Rows per page" selector (10/25/50), and Previous/Next with page number buttons.</p>
        </div>
      </section>

      {/* Section 5: Bottom Cards */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">5</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recent Activity, Quick Actions & System Health</h3>
        </div>
        <BottomCardsMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Three cards in a 3-column grid at the bottom of the page:</p>
          <p>• <strong>Recent Activity</strong> live feed of the latest 6 system events. Each event has a typed icon (green check for publish, blue upload for new email, orange pencil for edit, red circle for cancel, purple cpu for extraction). Click "View All" for the full audit log.</p>
          <p>• <strong>Quick Actions</strong> six navigation links with icon, title, subtitle, and arrow:
            <span className="block ml-4 mt-1">View All Requests, Email Queue, Templates, Activity Log, Documents, Settings</span>
          </p>
          <p>• <strong>System Health</strong> real-time monitoring of 5 backend services:
            <span className="block ml-4 mt-1">Database Latency (with progress bar, target &lt;100ms)</span>
            <span className="block ml-4">Azure Storage (Connected / Not Configured / Disconnected)</span>
            <span className="block ml-4">AI Service (Configured / Not Configured)</span>
            <span className="block ml-4">Queue Processing (Normal / Busy / Degraded)</span>
            <span className="block ml-4">Connection Pool (usage bar, e.g. "2 / 10")</span>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">System health refreshes every 5 minutes. A status badge (Healthy / Degraded / Unhealthy) is shown in the card header.</p>
        </div>
      </section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Silent Auto-Refresh</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">The dashboard refreshes data every 60 seconds in the background without showing loading spinners or resetting your filters, scroll position, or selections.</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Download className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Export Reports</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">Use the date filter + Export Report button to generate CSV reports for any time period. Great for monthly reporting or compliance audits.</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">High Pending Count?</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">If "Pending Reviews" stays elevated, check the Email Queue for stuck items that may need reprocessing, or verify that your AI Service is connected in System Health.</p>
          </div>
        </div>
      </section>

      {/* Related */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'email-queue', label: 'Email Queue', icon: Mail },
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'templates', label: 'Templates', icon: BarChart3 },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
